import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {TestIds} from '../PaymentsDetailsForm/utils';
import {CardBrand} from 'components/StripeCardElement/useStripeCardElement';
import {Status} from 'utils/types';
import {PaymentTypes} from '../../hooks/usePayment';
import {PATHS} from '../../Routes';
import {useRegisterSteps} from '../../hooks/useRegisterSteps';
import visaIcon from 'assets/icons/paymentVisa.svg';
import mastercardIcon from 'assets/icons/paymentMastercard.svg';
import Input from 'components/acommon/FieldInput';
import {FormFieldWrapper} from 'styled/common';
import {
  BasePaymentFormFields,
  BasePaymentFormWrapper,
  HeaderBrands,
  HeaderTitle,
  HeaderWrapper,
  SubmitButton,
  SubmitButtonMobileWrapper,
} from './styled';

export enum FORM_NAMES {
  cardNumber = 'cardNumber',
  cardHolderName = 'name',
  expirationDate = 'expirationDate',
  cvc = 'cvc',
  // saveCard = 'saveCard',
}

export type FormTypes = {
  [FORM_NAMES.cardHolderName]: string;
  // [FORM_NAMES.saveCard]: string;
};

export type HeaderProps = {
  brand?: CardBrand;
};
const Header = ({brand = 'unknown'}: HeaderProps) => {
  const {t} = useTranslation();
  const isVisaDisplayed = brand === 'visa' || brand === 'unknown';
  const isMCDisplayed = brand === 'mastercard' || brand === 'unknown';

  return (
    <HeaderWrapper>
      <HeaderTitle children={t('credit_card')} />
      <HeaderBrands>
        {isVisaDisplayed && <img src={visaIcon} alt="visa icon" />}
        {isMCDisplayed && <img src={mastercardIcon} alt="mastercard icon" />}
      </HeaderBrands>
    </HeaderWrapper>
  );
};

export type BasePaymentFormProps = HeaderProps & {
  paymentType: PaymentTypes;
  CardNumberElement?: JSX.Element;
  CardHolderNameElement?: JSX.Element;
  ExpirationDateElement?: JSX.Element;
  CVCElement?: JSX.Element;
  withoutSubmitButton?: boolean;
  status?: Status;
  disabledFields?: boolean;
  disabledSubmit?: boolean;
};
function BasePaymentForm(props: BasePaymentFormProps) {
  const {t} = useTranslation();
  const {count, getCurrentStep} = useRegisterSteps();
  const {
    register,
    formState: {errors},
  } = useFormContext<FormTypes>();
  const buttonLabel =
    props.paymentType === PaymentTypes.security_deposit ? t('pay_deposit') : t('pay');
  const currentStep = getCurrentStep(
    props.paymentType === PaymentTypes.security_deposit ? PATHS.protect : PATHS.pay,
  );

  const {
    CardNumberElement,
    ExpirationDateElement,
    CardHolderNameElement,
    CVCElement,
    withoutSubmitButton,
    disabledFields,
    disabledSubmit,
    status,
    ...other
  } = props;

  const formFields = React.useMemo(
    () => ({
      [FORM_NAMES.cardNumber]: () => (
        <FormFieldWrapper>
          <Input
            data-testid={TestIds.cardNumberInput}
            invalid={false}
            label={t('card_number')}
            placeholder={t('enter_number')}
            disabled={disabledFields}
          >
            {CardNumberElement}
          </Input>
        </FormFieldWrapper>
      ),
      [FORM_NAMES.expirationDate]: () => (
        <FormFieldWrapper>
          <Input
            placeholder={t('month') + '/' + t('year')}
            label={t('expiry_date')}
            disabled={disabledFields}
            data-testid={TestIds.cardExpirationDateInput}
            children={ExpirationDateElement}
          />
        </FormFieldWrapper>
      ),
      // ***********
      // Future the feature
      // [FORM_NAMES.saveCard]: () => (
      //   <FormFieldWrapper>
      //     <Checkbox
      //       name={FORM_NAMES.saveCard}
      //       // control={control}
      //       label={<span>{t('save_card')}</span>}
      //       checked={saveCard}
      //       onChange={() => toggleSaveCard(!saveCard)}
      //       // defaultValue={false}
      //        disabled={disabledFields}
      //       // error={errors[FORM_NAMES.saveCard]?.message}
      //     />
      //   </FormFieldWrapper>
      // ),
      // ***********
      [FORM_NAMES.cardHolderName]: () => (
        <FormFieldWrapper>
          <Input
            {...register(FORM_NAMES.cardHolderName)}
            label={t('cardholder_name_short')}
            placeholder={t('enter_name')}
            error={errors[FORM_NAMES.cardHolderName]?.message}
            disabled={disabledFields}
          />
        </FormFieldWrapper>
      ),
      [FORM_NAMES.cvc]: () => (
        <FormFieldWrapper>
          <Input label="CVC" placeholder="***" disabled={disabledFields}>
            {CVCElement}
          </Input>
        </FormFieldWrapper>
      ),
    }),
    [
      CVCElement,
      CardNumberElement,
      ExpirationDateElement,
      disabledFields,
      errors,
      register,
      t,
    ],
  );

  const FormFields = React.useMemo(
    function FormFieldsMemo() {
      return Object.keys(formFields).map(formName => (
        <React.Fragment key={formName}>
          {formFields[formName as FORM_NAMES]?.()}
        </React.Fragment>
      ));
    },
    [formFields],
  );

  return (
    <BasePaymentFormWrapper>
      <Header {...other} />
      <BasePaymentFormFields data-testid="fields">{FormFields}</BasePaymentFormFields>
      {!withoutSubmitButton && (
        <SubmitButtonMobileWrapper>
          <div className="progress">{`${currentStep}/${count}`}</div>
          <SubmitButton disabled={disabledSubmit} type="submit" label={buttonLabel} />
        </SubmitButtonMobileWrapper>
      )}
    </BasePaymentFormWrapper>
  );
}

export {BasePaymentForm};
